import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Box,
  InputAdornment,
  IconButton,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import React, { useEffect, useState } from "react";
import useUpdate from "../../../../api/useUpdate";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import useDelete from "../../../../api/useDelete";
import Status from "./Status";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ClearIcon from "@mui/icons-material/Clear";

const EditClient = ({ openEdit, onClose, data = null }) => {
  const { t } = useTranslation();
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const auth = useAuth();
  const token = auth?.user?.token;

  const [formData, setFormData] = useState({
    full_name: "",
    contact: "",
    contact_2: "",
    address: "",
    procedure: "",
    procedure_date: "",
    procedure_day: "",
    status: "",
    age: "",
    gender: "",
    price: "",
    advance: "",
    description: "",
  });

  const [formErrors, setFormErrors] = useState({});

  // Format datetime for datetime-local input
  const formatDateTimeForInput = (dateTimeString) => {
    if (!dateTimeString) return "";

    // If it's already in the correct format (from API), return as-is
    if (dateTimeString.includes("T")) {
      return dateTimeString.slice(0, 16); // Truncate to remove seconds if present
    }

    // Otherwise, create a new Date object and format it
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return "";

    const pad = (num) => num.toString().padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // Initialize form with data when component opens or data changes
  useEffect(() => {
    if (data && openEdit) {
      setFormData({
        full_name: data.full_name || "",
        contact: data.contact || "",
        contact_2: data.contact_2 || "",
        address: data.address || "",
        procedure: data.procedure || "",
        procedure_date: formatDateTimeForInput(data.procedure_date),
        procedure_day: data.procedure_day || "",
        status: data.status || "",
        age: data.age || "",
        gender: data.gender || "",
        price: data.price || "",
        advance: data.advance || "",
        description: data.description || "",
      });
    }
  }, [data, openEdit]);

  // Rest of your component remains the same...
  const {
    data: statusData,
    isLoading: isLoadingStatus,
    isError: isErrorStatus,
  } = useFetchObjects(["status"], "status", token);

  const {
    data: procedureTypes,
    isLoading: isLoadingType,
    isError: isErrorType,
  } = useFetchObjects(["procedure-type"], "procedure-type", token);

  const {
    handleUpdate,
    loading: loadingEdit,
    success,
  } = useUpdate("client", token);
  const { handleDelete, ConfirmDialog } = useDelete("status", token);

  useEffect(() => {
    if (success) {
      onClose();
    }
  }, [success, onClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const clearField = (fieldName) => {
    setFormData((prev) => ({ ...prev, [fieldName]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = {};

    if (!formData.full_name) errors.full_name = t("Full name is required");
    if (!formData.contact) errors.contact = t("Contact is required");

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const submitData = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value) submitData.append(key, value);
    });

    if (data?.id) {
      handleUpdate(data.id, submitData);
    }
  };

  return (
    <Dialog open={openEdit} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography
            variant="h5"
            gutterBottom
            fontWeight="bold"
            align="center"
            sx={{
              borderBottom: "2px solid",
              borderColor: "primary.main",
              pb: 2,
              mb: 3,
            }}
          >
            {t("Edit Client")}
          </Typography>

          <Grid container spacing={3} component="form" onSubmit={handleSubmit}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("Full Name")}
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                error={!!formErrors.full_name}
                helperText={formErrors.full_name}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("Contact Number")}
                name="contact"
                value={formData.contact}
                onChange={handleInputChange}
                error={!!formErrors.contact}
                helperText={formErrors.contact}
                required
                InputProps={{
                  endAdornment: formData.contact && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => clearField("contact")}
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("Secondary Contact")}
                name="contact_2"
                value={formData.contact_2}
                onChange={handleInputChange}
                InputProps={{
                  endAdornment: formData.contact_2 && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => clearField("contact_2")}
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("Address")}
                name="address"
                value={formData.address}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Procedure Information */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label={t("Procedure")}
                name="procedure"
                value={formData.procedure}
                onChange={handleInputChange}
                InputProps={{
                  endAdornment: formData.procedure && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => clearField("procedure")}
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              >
                {isLoadingType ? (
                  <MenuItem disabled>
                    <CircularProgress size={24} />
                  </MenuItem>
                ) : isErrorType ? (
                  <MenuItem disabled>
                    <Typography color="error">
                      {t("Error loading procedures")}
                    </Typography>
                  </MenuItem>
                ) : (
                  procedureTypes?.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label={t("Procedure Date & Time")}
                name="procedure_date"
                value={formData.procedure_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label={t("Procedure Day")}
                name="procedure_day"
                value={formData.procedure_day}
                onChange={handleInputChange}
                InputProps={{
                  endAdornment: formData.procedure_day && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => clearField("procedure_day")}
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="">{t("None")}</MenuItem>
                {[
                  "monday",
                  "tuesday",
                  "wednesday",
                  "thursday",
                  "friday",
                  "saturday",
                  "sunday",
                ].map((day) => (
                  <MenuItem key={day} value={day}>
                    {t(day.charAt(0).toUpperCase() + day.slice(1))}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Status Section */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label={t("Status")}
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                InputProps={{
                  endAdornment: formData.status && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => clearField("status")}
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="">{t("Clear")}</MenuItem>
                {isLoadingStatus ? (
                  <MenuItem disabled>
                    <CircularProgress size={24} />
                  </MenuItem>
                ) : isErrorStatus ? (
                  <MenuItem disabled>
                    <Typography color="error">
                      {t("Error loading statuses")}
                    </Typography>
                  </MenuItem>
                ) : (
                  statusData?.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <span>{item.name}</span>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item.id);
                          }}
                          size="small"
                          sx={{ color: "error.main" }}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </TextField>
              <Button
                fullWidth
                variant="outlined"
                sx={{ mt: 1 }}
                onClick={() => setOpenStatusDialog(true)}
              >
                {t("Add New Status")}
              </Button>
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label={t("Age")}
                name="age"
                value={formData.age}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label={t("Gender")}
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
              >
                <MenuItem value="">{t("Clear")}</MenuItem>
                <MenuItem value={1}>{t("Male")}</MenuItem>
                <MenuItem value={2}>{t("Female")}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label={t("Price")}
                name="price"
                value={formData.price}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t("Advance")}
                name="advance"
                value={formData.advance}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t("Description")}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
            >
              <LoadingButton
                loading={loadingEdit}
                variant="contained"
                type="submit"
                sx={{ mr: 2 }}
              >
                {t("Save Changes")}
              </LoadingButton>
              <Button variant="outlined" onClick={onClose}>
                {t("Cancel")}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>
      <ConfirmDialog />
      <Status
        open={openStatusDialog}
        onClose={() => setOpenStatusDialog(false)}
        token={token}
      />
    </Dialog>
  );
};

export default EditClient;
